import { ReactElement, useCallback, useMemo } from 'react'
import _ from 'lodash'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTheme } from '@emotion/react'

// constants
import { AVATAR_SIZES, USER_EDIT_MODE, USER_ROLE_LABELS } from 'constants/user'
import { FEATURES } from 'constants/settings'

// utils
import { useAuthStateValue } from 'contexts'
import { useFeatureFlag, useUserAbility } from 'hooks'
import { showError } from 'helpers/message'
import { getGroupDisplayName } from 'helpers/user'

import type { User, UserSettingMode } from 'types/user'

// components
import { MultiSelect } from 'components/common'
import UserInformationForm from './UserInformationForm'
import UserTimeZoneSetting from './UserTimeZoneSetting'
import UserInformationCard from './UserInformationCard'
import EmissionEventsUnits from './EmissionEventsUnits'

import scss from '../index.module.scss'

type AccordionRowProps = {
  label: string
  children: React.ReactNode
  expandedAccordionProps: {
    elevation: number
    expanded: boolean
    sx: Record<string, unknown>
  }
  accordionContentStyle: React.CSSProperties
}

const AccordionRow = ({
  label,
  children,
  expandedAccordionProps,
  accordionContentStyle,
}: AccordionRowProps) => (
  <Accordion {...expandedAccordionProps}>
    <AccordionSummary>
      <div style={accordionContentStyle}>
        <Typography>{label}</Typography>
        {children}
      </div>
    </AccordionSummary>
  </Accordion>
)

type SectionProps = {
  title: string
  children: React.ReactNode
}

const Section = ({ title, children }: SectionProps) => (
  <>
    <div className='pillLabel text-secondary'>{title}</div>
    <div className={scss.section}>{children}</div>
  </>
)

const UserProfile = ({
  user,
  onChange,
  onModeChange,
}: {
  user: User
  onChange: (v: Partial<User>) => void
  onModeChange: (v: UserSettingMode) => void
}): ReactElement => {
  const { role, group, externalUsername } = user

  const { userGroupsOptions, sessionUserGroup, updateCurrentUserSessionGroup } =
    useAuthStateValue()

  const { [FEATURES.EMISSION_EVENTS]: emissionEventEnabled } = useFeatureFlag()

  const { 'secondary-100': secondary100 } = useTheme()

  const onChangeSessionUserGroup = useCallback(
    async (value: string) => {
      try {
        await updateCurrentUserSessionGroup(value)
      } catch (err) {
        showError((err as Error).message)
      }
    },
    [updateCurrentUserSessionGroup]
  )

  const userGroupOptions = useMemo(
    () =>
      externalUsername ? [{ value: group, label: group }] : userGroupsOptions,
    [externalUsername, group, userGroupsOptions]
  )

  const { canUpdate } = useUserAbility({ user })

  const baseAccordionProps = {
    elevation: 0,
    sx: {
      background: 'white',
      border: `1px solid ${secondary100}`,
      '&:before': { display: 'none' },
    },
  }

  const expandedAccordionProps = {
    ...baseAccordionProps,
    expanded: true,
  }

  const accordionContentStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  }

  return (
    <>
      <Section title='YOUR INFORMATION'>
        <Accordion {...baseAccordionProps}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} component='div'>
            <UserInformationCard
              user={user}
              avatarSize={AVATAR_SIZES.xLarge}
              onClickAvatar={e => {
                e?.stopPropagation()
                onModeChange(USER_EDIT_MODE.avatar)
              }}
            />
          </AccordionSummary>
          <AccordionDetails>
            <UserInformationForm user={user} onChange={onChange} />
          </AccordionDetails>
        </Accordion>
      </Section>

      <Section title='TIME ZONE SETTING'>
        <AccordionRow
          label='Time zone'
          expandedAccordionProps={expandedAccordionProps}
          accordionContentStyle={accordionContentStyle}
        >
          <UserTimeZoneSetting
            user={user}
            onChange={onChange}
            className={scss.select}
          />
        </AccordionRow>
      </Section>

      {emissionEventEnabled && (
        <Section title='Units'>
          <Accordion {...baseAccordionProps} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Emission Events</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <EmissionEventsUnits user={user} onChange={onChange} />
            </AccordionDetails>
          </Accordion>
        </Section>
      )}

      <Section title='USER ASSIGNMENT'>
        <AccordionRow
          label='Role'
          expandedAccordionProps={expandedAccordionProps}
          accordionContentStyle={accordionContentStyle}
        >
          <span className={scss.accordionAddOnText}>
            {_.startCase(USER_ROLE_LABELS[role])}
          </span>
        </AccordionRow>

        <AccordionRow
          label='User Group'
          expandedAccordionProps={expandedAccordionProps}
          accordionContentStyle={accordionContentStyle}
        >
          <span className={scss.accordionAddOnText}>
            {getGroupDisplayName({ userGroupsOptions, group })}
          </span>
        </AccordionRow>

        {userGroupsOptions?.length > 1 && (
          <AccordionRow
            label='Customer Group'
            expandedAccordionProps={expandedAccordionProps}
            accordionContentStyle={accordionContentStyle}
          >
            <MultiSelect
              placeholder='View screens as this customer group'
              value={sessionUserGroup}
              options={userGroupOptions}
              onChange={async v => {
                onChangeSessionUserGroup(v as string)
              }}
              isMulti={false}
              isClearable={false}
              isDisabled={!canUpdate}
              useOptionValueOnly
              withBorder
              className={scss.select}
            />
          </AccordionRow>
        )}
      </Section>
    </>
  )
}

export default UserProfile
